import React, { useCallback, useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import Table from 'components/Table';
import dayjs from 'dayjs';
import useAthleteBirthdays from 'hooks/reports/athletes/useAthleteBirthdays';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteBirthdayReport } from 'types';

import { SearchFilters } from '../types';

const AthleteBirthdaysTable = ({
  searchFilters,
  setOrderingValue,
}: {
  searchFilters: SearchFilters;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const navigate = useNavigate();

  const [tableData, setTableData] = useState<AthleteBirthdayReport[]>([]);
  const [ordering, setOrdering] = useState('dob');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthleteBirthdays({
      dateStart: searchFilters.dateRange[0]
        ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD')
        : undefined,
      dateEnd: searchFilters.dateRange[1]
        ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD')
        : undefined,
      athleteStatuses: searchFilters.athleteStatuses,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  const handleNameClick = useCallback(
    (athlete: AthleteBirthdayReport): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<AthleteBirthdayReport>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleNameClick(row.original)}
        >
          {row.original.fullName}
        </Text>
      ),
    },
    {
      Header: 'Current Status',
      accessor: 'athleteStatus' as const,
      disableSortBy: true,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
      disableSortBy: true,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      disableSortBy: true,
    },
    {
      Header: 'Date of Birth',
      accessor: 'dob' as const,
      Cell: ({ row }: CellProps<AthleteBirthdayReport>) => (
        <Text>{dayjs(row.original.dob).format('MMMM D, YYYY')}</Text>
      ),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'dob']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
    </>
  );
};

export default AthleteBirthdaysTable;
